import React from 'react';
import { Link } from 'react-router-dom';
import { useCustom } from '@refinedev/core';
import { Collapse, Descriptions, Space, Table, Tag, theme } from 'antd';
import { ReferralPartyDto, UserReferralsDto } from '@wellverse/shared-ts';

/**
 * Who referred this user, and who this user has referred.
 *
 * A referral code is another user's `friendCode`; the `referrals` table records
 * the resolved link when onboarding completes. A user who typed a code that
 * matched nobody has a `UserProfile.referralCode` but no row here, which is why
 * the two can disagree.
 */
export const UserReferrals: React.FC<{ userId: string }> = ({ userId }) => {
  const { token } = theme.useToken();

  const { data, isLoading } = useCustom<UserReferralsDto>({
    url: `/users/${userId}/referrals`,
    method: 'get',
  });

  const referrals = data?.data;
  const referredBy = referrals?.referredBy;
  const referred = referrals?.referred ?? [];

  const userLink = (party: ReferralPartyDto) => (
    <Link to={`/users/show/${party.userId}`}>{party.fullName || party.userId}</Link>
  );

  return (
    <Collapse
      style={{ marginTop: 24 }}
      items={[
        {
          key: 'referrals',
          label: (
            <Space>
              Referrals
              <span style={{ color: token.colorTextSecondary, fontWeight: 400 }}>
                {referredBy ? 'referred · ' : ''}
                {referrals?.referredCount ?? 0} invited
              </span>
            </Space>
          ),
          children: (
            <>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Referred By">
                  {referredBy ? (
                    <Space>
                      {userLink(referredBy)}
                      <Tag>{referredBy.code}</Tag>
                    </Space>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Referred On">
                  {referredBy ? new Date(referredBy.createdAt).toLocaleString() : '—'}
                </Descriptions.Item>
              </Descriptions>

              <div style={{ margin: '16px 0 8px', color: token.colorTextSecondary }}>
                Users who signed up with this user&apos;s code
              </div>
              <Table
                dataSource={referred}
                loading={isLoading}
                rowKey="userId"
                pagination={false}
                size="small"
                locale={{ emptyText: 'Nobody has used this code yet.' }}
              >
                <Table.Column
                  title="User"
                  render={(_, party: ReferralPartyDto) => userLink(party)}
                />
                <Table.Column dataIndex="friendCode" title="Their Code" width={140} />
                <Table.Column dataIndex="code" title="Code Used" width={140} />
                <Table.Column
                  dataIndex="createdAt"
                  title="Referred On"
                  width={200}
                  render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
                />
              </Table>
            </>
          ),
        },
      ]}
    />
  );
};
